import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { PROTOCOLS, getProtocol } from "./protocols/definitions";
import { PRO_PRICE_IDS, FEATURE_TIER_PRO_ENABLED } from "./feature-flags";

const PROTOCOL_GOALS = PROTOCOLS.map((p) => p.goal) as [string, ...string[]];

export const startProtocol = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      goal: z.enum(PROTOCOL_GOALS),
      country: z.string().length(2).optional(),
      jurisdiction: z.string().min(2).max(16).optional(),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const def = getProtocol(data.goal)!;
    // --- Tier gating ---------------------------------------------------
    // Core protocols require any active subscription (or the free tier
    // rules already enforced upstream). Pro protocols require an active
    // sub whose price_id matches one of PRO_PRICE_IDS.
    if (def.tier === "pro" && FEATURE_TIER_PRO_ENABLED && PRO_PRICE_IDS.length > 0) {
      const { data: subs } = await supabase
        .from("subscriptions")
        .select("price_id, status")
        .eq("user_id", userId)
        .in("status", ["active", "trialing"]);
      const hasPro = (subs ?? []).some((s: any) => s.price_id && PRO_PRICE_IDS.includes(s.price_id));
      if (!hasPro) {
        throw new Error("pro_tier_required");
      }
    }
    // Reuse current active session if any.
    const { data: existing } = await supabase
      .from("protocol_sessions")
      .select("id")
      .eq("user_id", userId)
      .eq("goal", data.goal)
      .eq("status", "active")
      .maybeSingle();
    if (existing?.id) return { sessionId: existing.id as string, reused: true };
    const { data: row, error } = await supabase
      .from("protocol_sessions")
      .insert({
        user_id: userId,
        goal: data.goal,
        current_step: def.steps[0]?.key ?? null,
        steps_json: def.steps.map((s) => ({ key: s.key, dayOffset: s.dayOffset })),
        country_at_start: data.country ?? null,
        jurisdiction_at_start: data.jurisdiction ?? null,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { sessionId: row.id as string, reused: false };
  });

export const completeProtocolStep = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      sessionId: z.string().uuid(),
      stepKey: z.string().min(1).max(64),
      notes: z.string().max(2000).optional(),
      analysisId: z.string().uuid().optional(),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("protocol_step_events")
      .insert({
        session_id: data.sessionId,
        user_id: userId,
        step_key: data.stepKey,
        notes: data.notes ?? null,
        analysis_id: data.analysisId ?? null,
      });
    if (error) throw new Error(error.message);
    await supabase
      .from("protocol_sessions")
      .update({ current_step: data.stepKey })
      .eq("id", data.sessionId)
      .eq("user_id", userId);
    return { ok: true };
  });

export const getMyProtocols = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const [{ data: sessions }, { data: events }] = await Promise.all([
      supabase
        .from("protocol_sessions")
        .select("id, goal, started_at, current_step, status, extended")
        .eq("user_id", userId)
        .order("started_at", { ascending: false }),
      supabase
        .from("protocol_step_events")
        .select("id, session_id, step_key, done_at")
        .eq("user_id", userId),
    ]);
    return { sessions: sessions ?? [], events: events ?? [] };
  });

/**
 * Opt-in: marks the active session as `extended`, unlocking the protocol's
 * `extension.steps`. No-op if the protocol has no extension defined.
 */
export const extendProtocolSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ sessionId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("protocol_sessions")
      .update({ extended: true })
      .eq("id", data.sessionId)
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const undoLastProtocolStep = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ sessionId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    // Find the most recent step event for this session
    const { data: last, error: selErr } = await supabase
      .from("protocol_step_events")
      .select("id, step_key")
      .eq("user_id", userId)
      .eq("session_id", data.sessionId)
      .order("done_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (selErr) throw new Error(selErr.message);
    if (!last) return { ok: true, undone: null as string | null };
    const { error: delErr } = await supabase
      .from("protocol_step_events")
      .delete()
      .eq("id", last.id)
      .eq("user_id", userId);
    if (delErr) throw new Error(delErr.message);
    // Update current_step to the new most-recent remaining event (or null)
    const { data: prev } = await supabase
      .from("protocol_step_events")
      .select("step_key")
      .eq("user_id", userId)
      .eq("session_id", data.sessionId)
      .order("done_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    await supabase
      .from("protocol_sessions")
      .update({ current_step: prev?.step_key ?? null })
      .eq("id", data.sessionId)
      .eq("user_id", userId);
    return { ok: true, undone: last.step_key as string };
  });

/**
 * Returns a signed URL (valid 1h) for the most recent stored selfie of
 * a given step in the user's active session for this goal — used by the
 * Capture Coach to render a ghost overlay for re-framing.
 */
export const getLastStepImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      goal: z.enum(PROTOCOL_GOALS),
      stepKey: z.string().min(1).max(64),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: session } = await supabase
      .from("protocol_sessions")
      .select("id")
      .eq("user_id", userId)
      .eq("goal", data.goal)
      .eq("status", "active")
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!session?.id) return { url: null as string | null };

    const { data: row } = await supabase
      .from("protocol_step_events")
      .select("image_path")
      .eq("user_id", userId)
      .eq("session_id", session.id)
      .eq("step_key", data.stepKey)
      .not("image_path", "is", null)
      .order("done_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!row?.image_path) return { url: null as string | null };

    const { data: signed } = await supabase.storage
      .from("selfies")
      .createSignedUrl(row.image_path as string, 60 * 60);
    return { url: signed?.signedUrl ?? null };
  });
